import type { SubAgency, AgencyUserWithAgency } from "../../types/types";

import { useGetSubAgencies, useGetSubAgencyStats, useGetSubAgencyTree } from "../../hooks/use-hook";

import { SubAgenciesTable } from "./SubAgenciesTable";

interface SubAgenciesContainerProps {
  parentAgencyId?: string;
  parentAgencyName?: string;
  onViewDetails: (subAgency: SubAgency) => void;
  onViewAgentDetails?: (agencyUser: AgencyUserWithAgency) => void;
  period?: '6-months' | '12-months' | 'year-to-date';
  startDate?: string;
  endDate?: string;
}

export const SubAgenciesContainer = function({
  parentAgencyId,
  parentAgencyName,
  onViewDetails,
  onViewAgentDetails,
  period = '6-months',
  startDate,
  endDate,
}: SubAgenciesContainerProps) {
  const { data, isLoading, error } = useGetSubAgencies({
    parentAgencyId,
    page: 1,
    limit: 50,
  });

  const { data: statsData, isLoading: isLoadingStats } = useGetSubAgencyStats(
    parentAgencyId || '',
    period,
    startDate,
    endDate
  );

  // Full descendant tree with subtree-rolled-up stats (premium/policies/agents
  // combined across every depth) — powers the expandable hierarchy view.
  const { data: treeData } = useGetSubAgencyTree(
    parentAgencyId || '',
    period,
    startDate,
    endDate,
  );

  // Handle errors more gracefully - don't show errors for 404s
  if (error) {
    let errorMessage = 'An unknown error occurred';

    // Extract error message properly from different error formats
    if (error instanceof Error) {
      errorMessage = error.message;
    } else if (typeof error === 'object' && error !== null) {
      // Handle transformed error objects
      const errorObj = error as any;
      errorMessage = errorObj.message || errorObj.error || JSON.stringify(error);
    } else if (typeof error === 'string') {
      errorMessage = error;
    }

    // If it's a 404 error, treat as no data instead of error
    if (errorMessage?.includes('404')) {
      // Continue to render with empty data instead of showing error
    } else {

      return (
        <div className="flex items-center justify-center py-8">
          <div className="text-red-600">Error loading sub agencies: {errorMessage}</div>
        </div>
      );
    }
  }

  // Ensure we have the correct data structure
  const normalize = (input: unknown): SubAgency[] => {
    if (Array.isArray(input)) return (input as unknown as SubAgency[]).filter(item => item != null);
    if (input && typeof input === "object") {
      const obj = input as Record<string, unknown>;
      if (Array.isArray(obj.items)) return (obj.items as unknown as SubAgency[]).filter(item => item != null);
      if (obj.data && typeof obj.data === "object") {
        const d = obj.data as Record<string, unknown>;
        if (Array.isArray(d.items)) return (d.items as unknown as SubAgency[]).filter(item => item != null);
      }
    }
    return [];
  };
  const subAgencies: SubAgency[] = normalize(data);

  // Merge sub-agencies with their stats
  const subAgenciesWithStats = subAgencies.map(agency => {
    const stats = Array.isArray(statsData)
      ? statsData.find(stat => stat.id === agency.id)
      : null;

    // Calculate agent count excluding owners (parent agency owner is auto-assigned)
    const nonOwnerAgentCount = agency.agencyUsers?.filter(
      (au: any) => au.role?.toLowerCase() !== 'owner'
    ).length || 0;

    return {
      ...agency,
      // OWNER-NAME FIX (2026-07-22): the flat stats endpoint carries ownerName
      // (local owner, upline-inherited owners excluded), but the plain
      // sub-agencies list does not — so top-level rows showed "—" while nested
      // tree rows (which read ownerName straight off the tree node) showed the
      // real owner. Lift it from stats onto the row the table renders.
      ownerName: (stats as any)?.ownerName || (agency as any).ownerName || '',
      stats: stats || {
        totalPremium: 0,
        totalPolicies: 0,
        averagePolicySize: 0,
        closingRatio: 0,
        agentCount: nonOwnerAgentCount,
        changePercentage: 0,
      }
    };
  });

  return (
    <div data-tour="sub-agencies-content">
      <SubAgenciesTable
        isLoading={isLoading || isLoadingStats}
        onViewDetails={onViewDetails}
        onViewAgentDetails={onViewAgentDetails}
        subAgencies={subAgenciesWithStats}
        parentAgencyId={parentAgencyId}
        parentAgencyName={parentAgencyName}
        treeNodes={Array.isArray(treeData) ? treeData : []}
        period={period}
        startDate={startDate}
        endDate={endDate}
      />
    </div>
  );
};

